import React, { useCallback, useEffect, useState } from 'react';
import {
  useSelector as useReduxSelector,
  TypedUseSelectorHook,
} from 'react-redux';
import { AppState, useAppDispatch } from 'stores/type';
import { SignUpPageLayout } from 'views/layouts/SignUpPageLayout';
import { DefaultTemplate } from 'views/templates/DefaultTemplate';
import { Redirect } from 'react-router-dom';
import { DeviceDomain } from 'models/Device';
import { useIntl } from 'react-intl';
import queryString from 'query-string';
import { Splash } from 'views/components/molecules/Splash';
import { setExceptionError } from 'stores/errorSlice';
import { ROUTER_PATH } from 'views/constants';
import { useEmailVerification } from 'views/hooks/useEmailVerification';
import { useInvitation } from 'views/hooks/useInvitation';
import { useDevice } from 'views/hooks/useDevice';
import { ApplicationError } from 'models/Error';

export const SignUpPage: React.FC = () => {
  const oauthSessionToken = queryString.parse(location.search)[
    'ios_spa_onetime_token'
  ] as string;

  const presetEmail = queryString.parse(location.search)['email'] as string;
  const presetName = queryString.parse(location.search)['name'] as string;
  const invitationToken = queryString.parse(location.search)[
    'invitation_token'
  ] as string;

  const dispatch = useAppDispatch();
  const useSelector: TypedUseSelectorHook<AppState> = useReduxSelector;
  const device = useSelector((state) => state.deviceState.device);
  const deviceDomain = new DeviceDomain(device);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [nameErrorMessage, setNameErrorMessage] = useState<string>();
  const [passwordErrorMessage, setPasswordErrorMessage] = useState<string>();
  const locale = window.navigator.language.slice(0, 2);
  const intl = useIntl();
  const [isInvitationLoading, invitation] = useInvitation(invitationToken);
  const [
    emailVerificationTransactionKey,
    verifyingEmail,
    verifiedEmail,
    emailVerificationErrorMessage,
    sendVerificationCode,
    reSendVerificationCode,
    verifyEmail,
  ] = useEmailVerification();
  const { signUp } = useDevice();

  useEffect(() => {
    setErrorMessage(emailVerificationErrorMessage);
  }, [emailVerificationErrorMessage]);

  const handleSignUp = useCallback(
    async (password: string, username: string, termsAccept: boolean) => {
      if (
        !invitation ||
        emailVerificationTransactionKey === undefined ||
        verifiedEmail === undefined ||
        !termsAccept
      ) {
        return;
      }

      await signUp(
        emailVerificationTransactionKey,
        password,
        username,
        locale,
        termsAccept,
        invitation?.token,
        oauthSessionToken
      )
        .then(() => {
          setNameErrorMessage('');
          setPasswordErrorMessage('');
        })
        .catch((e) => {
          if (e instanceof ApplicationError) {
            if (e.errorDetails?.name) {
              let mes = '';
              for (const err of e.errorDetails.name) {
                mes += intl.formatMessage({
                  id: e.localeId('name.' + err.error),
                });
                mes += ' ';
              }
              setNameErrorMessage(mes);
            }
            if (e.errorDetails?.password) {
              let mes = '';
              for (const err of e.errorDetails.password) {
                mes += intl.formatMessage({
                  id: e.localeId('password.' + err.error),
                });
                mes += ' ';
              }
              setPasswordErrorMessage(mes);
            }
          } else {
            dispatch(setExceptionError(e));
          }
        });
    },
    [
      emailVerificationTransactionKey,
      verifiedEmail,
      signUp,
      locale,
      invitation,
      oauthSessionToken,
      intl,
      dispatch,
    ]
  );

  if (
    deviceDomain.isAuthenticated() ||
    deviceDomain.hasIphoneRegistrationToken()
  ) {
    return <Redirect to={ROUTER_PATH.apps} />;
  } else if (isInvitationLoading) {
    return (
      <DefaultTemplate headerText="">
        <Splash />
      </DefaultTemplate>
    );
  } else if (!invitation) {
    return <Redirect to={ROUTER_PATH.login} />;
  } else {
    return (
      <DefaultTemplate headerText="DeployGate" showLogo>
        <SignUpPageLayout
          verifiedEmail={verifiedEmail}
          verifyingEmail={verifyingEmail}
          errorMessage={errorMessage}
          nameErrorMessage={nameErrorMessage}
          passwordErrorMessage={passwordErrorMessage}
          onSignUp={handleSignUp}
          onSendVerificationEmail={(email) => {
            sendVerificationCode(email);
          }}
          onReSendVerificationEmail={reSendVerificationCode}
          onValidateTransaction={(code) => {
            verifyEmail(code);
          }}
          presetEmail={presetEmail || invitation?.email}
          presetUsername={presetName}
        />
      </DefaultTemplate>
    );
  }
};
